/**
 * shared/collision/format.ts — parse `bo2sns-collision-1` (collision.json + collision.bin, written by
 * tools/map/export_collision.py; layout documented in tools/map/README.md "collision.bin / collision.json").
 *
 * OWNER: collision agent. Pure (no DOM/Node): runs in Node (server), the Worker and the browser.
 *
 * Everything in the file is in **three.js space** (Y-up). The parser returns raw typed-array views;
 * conversion to CoD space happens once in build.ts (`cod = (x, -z, y)`, normals likewise, plane dist
 * unchanged — tools/map/README.md "Conventions"). Float32 → float64 promotion and negation are exact,
 * so the converted data is bit-identical on every JS engine.
 */

export interface CollisionSection {
  offset: number;
  byteLength: number;
  count: number;
  dtype: string;
  layout?: string;
}

/** Subset of collision.json we rely on. */
export interface CollisionJson {
  format: string;
  bin?: string;
  sections: Record<string, CollisionSection>;
  counts?: Record<string, number>;
  playerMask?: number;
  [k: string]: unknown;
}

/** Raw (three-space) views over collision.bin. */
export interface RawCollision {
  /** float32 xyz per vertex (three space). */
  positions: Float32Array;
  /** uint32 x3 per triangle. */
  indices: Uint32Array;
  triContents: Uint32Array;
  triSurfaceFlags: Uint32Array;
  /** 0 brush face, 1 clipMap triangle (patch/terrain/model clip), 2 static-model XModelCollSurf triangle. */
  triSource: Uint8Array;
  /** brush index | clipMap tri index | static model index. */
  triOwner: Int32Array;
  /** 0 world, N = submodel *N, -1 unreferenced. */
  triModel: Int16Array;
  /** uint32 x5 per brush: contents, model (int32), firstPlane, planeCount, firstTriangle. */
  brushes: Uint32Array;
  /** float32 x6 per brush: three-space AABB min xyz, max xyz. */
  brushBounds: Float32Array;
  /** 6 x 4 bytes per plane: float32 normal xyz (outward, three space), float32 dist, uint32 cflags, uint32 sflags. */
  planeF: Float32Array;
  planeU: Uint32Array;
  numTris: number;
  numBrushes: number;
  numPlanes: number;
}

export const COLLISION_FORMAT = 'bo2sns-collision-1';

function section(j: CollisionJson, name: string): CollisionSection {
  const s = j.sections[name];
  if (!s) throw new Error(`collision.json: missing section '${name}'`);
  return s;
}

/** Copy a section into an aligned, little-endian typed array (the file is LE; every target we run on is LE). */
function view<T extends Float32Array | Uint32Array | Int32Array | Int16Array | Uint8Array>(
  bin: ArrayBuffer, s: CollisionSection,
  Ctor: { new (b: ArrayBuffer, off: number, len: number): T; readonly BYTES_PER_ELEMENT: number },
): T {
  const n = s.byteLength / Ctor.BYTES_PER_ELEMENT;
  if (s.offset + s.byteLength > bin.byteLength) throw new Error('collision.bin truncated');
  if (s.offset % Ctor.BYTES_PER_ELEMENT === 0) return new Ctor(bin, s.offset, n);
  // unaligned (should not happen: sections are 16-byte aligned) -> copy
  const copy = bin.slice(s.offset, s.offset + s.byteLength);
  return new Ctor(copy, 0, n);
}

export function parseCollision(j: CollisionJson, bin: ArrayBuffer): RawCollision {
  if (j.format !== COLLISION_FORMAT) throw new Error(`collision.json: unsupported format '${j.format}'`);
  // Endianness guard: collision.bin is little-endian.
  if (new Uint8Array(new Uint32Array([1]).buffer)[0] !== 1) throw new Error('big-endian hosts are not supported');
  const pos = section(j, 'positions');
  const idx = section(j, 'indices');
  const br = section(j, 'brushes');
  const pl = section(j, 'brushPlanes');
  const planeBuf = view(bin, pl, Float32Array);
  const raw: RawCollision = {
    positions: view(bin, pos, Float32Array),
    indices: view(bin, idx, Uint32Array),
    triContents: view(bin, section(j, 'triContents'), Uint32Array),
    triSurfaceFlags: view(bin, section(j, 'triSurfaceFlags'), Uint32Array),
    triSource: view(bin, section(j, 'triSource'), Uint8Array),
    triOwner: view(bin, section(j, 'triOwner'), Int32Array),
    triModel: view(bin, section(j, 'triModel'), Int16Array),
    brushes: view(bin, br, Uint32Array),
    brushBounds: view(bin, section(j, 'brushBounds'), Float32Array),
    planeF: planeBuf,
    planeU: new Uint32Array(planeBuf.buffer, planeBuf.byteOffset, planeBuf.length),
    numTris: idx.count,
    numBrushes: br.count,
    numPlanes: pl.count,
  };
  if (raw.indices.length !== raw.numTris * 3) throw new Error('collision.bin: index count mismatch');
  if (raw.brushes.length !== raw.numBrushes * 5) throw new Error('collision.bin: brush count mismatch');
  if (raw.planeF.length !== raw.numPlanes * 6) throw new Error('collision.bin: plane count mismatch');
  return raw;
}
